import { Data, Schema } from "effect";
import { RecoveryReceiptSchema } from "@ascleo/frontdesk-contracts/scheduling/recovery";
import { dateFromEpochMillis, epochMillisOfUtcInstant, formatUtcInstant, type Clock } from "@ascleo/types/date-time-validity";

const retentionMs = 24 * 60 * 60 * 1000;
const prefix = "frontdesk-booking-recovery:";
const StoredReceipt = Schema.Struct({
  clientSubmissionId: Schema.String,
  recoveryReceipt: RecoveryReceiptSchema,
  createdAt: Schema.String,
  expiresAt: Schema.String,
});
const ExpiredReceipt = Schema.Struct({
  _tag: Schema.Literal('ExpiredBookingRecovery'), clientSubmissionId: Schema.String,
  createdAt: Schema.String, expiresAt: Schema.String,
});

/** Minimal dedicated durable browser storage used by receipt preparation and recovery.
 * @since 1.1.8
 * @example const storage: BookingRecoveryStorage = localStorage;
 */
export type BookingRecoveryStorage = Pick<Storage, "getItem" | "setItem" | "removeItem">;
/** Recovery metadata survives ordinary patient-session reset without storing note or answers.
 * @since 1.1.8
 * @example const receipt: StoredBookingRecovery = { clientSubmissionId: "s", recoveryReceipt: "ab".repeat(32), createdAt: "2026-10-05T09:00:00.000Z", expiresAt: "2026-10-06T09:00:00.000Z" };
 */
export type StoredBookingRecovery = Schema.Schema.Type<typeof StoredReceipt>;
/** Receipt persistence failures prevent new dispatch rather than degrading to page memory.
 * @since 1.1.8
 * @example new BookingRecoveryStorageError({ reason: "storage-unavailable" });
 */
export class BookingRecoveryStorageError extends Data.TaggedError("BookingRecoveryStorageError")<{
  readonly reason: "storage-unavailable" | "missing" | "expired" | "malformed";
}> {}

/** Retrieve an existing receipt for status/retry without ever minting a replacement.
 * @since 1.1.8
 * @example readBookingRecovery(localStorage, clock, "submission-1");
 */
export function readBookingRecovery(storage: BookingRecoveryStorage, clock: Clock, clientSubmissionId: string): StoredBookingRecovery {
  let raw: string | null;
  try { raw = storage.getItem(prefix + clientSubmissionId); }
  catch { throw new BookingRecoveryStorageError({ reason: "storage-unavailable" }); }
  if (raw === null) throw new BookingRecoveryStorageError({ reason: "missing" });
  let record: Schema.Schema.Type<typeof StoredReceipt> | Schema.Schema.Type<typeof ExpiredReceipt>;
  try {
    record = Schema.decodeUnknownSync(Schema.Union([StoredReceipt, ExpiredReceipt]))(JSON.parse(raw));
    const created = epochMillisOfUtcInstant(record.createdAt);
    const expires = epochMillisOfUtcInstant(record.expiresAt);
    if (record.clientSubmissionId !== clientSubmissionId || expires - created !== retentionMs) throw new Error("Invalid receipt binding");
  } catch { throw new BookingRecoveryStorageError({ reason: "malformed" }); }
  if ('_tag' in record || epochMillisOfUtcInstant(formatUtcInstant(clock.now())) >= epochMillisOfUtcInstant(record.expiresAt)) {
    // Retain only expiry proof until the controller finishes generation/pointer
    // retirement. Storage failures can retry without retaining the capability.
    const tombstone = JSON.stringify({ _tag: 'ExpiredBookingRecovery', clientSubmissionId, createdAt: record.createdAt, expiresAt: record.expiresAt });
    try {
      storage.setItem(prefix + clientSubmissionId, tombstone);
      if (storage.getItem(prefix + clientSubmissionId) !== tombstone) throw new Error('Expiry metadata was not persisted');
    } catch { throw new BookingRecoveryStorageError({ reason: 'storage-unavailable' }); }
    throw new BookingRecoveryStorageError({ reason: "expired" });
  }
  return record;
}

/** Mint a fresh submission and 32-byte receipt, then verify durable storage before dispatch.
 * This function accepts no old submission ID; missing recovery never creates a
 * replacement secret for an uncertain old intent. Ordinary session reset must not
 * clear this dedicated store. Local expiry permits only a fresh confirmed flow.
 * @since 1.1.8
 * @example const receipt = prepareNewBookingRecovery(localStorage, clock);
 */
export function prepareNewBookingRecovery(storage: BookingRecoveryStorage, clock: Clock): StoredBookingRecovery {
  const createdAt = formatUtcInstant(clock.now());
  const bytes = globalThis.crypto.getRandomValues(new Uint8Array(32));
  const record: StoredBookingRecovery = {
    clientSubmissionId: globalThis.crypto.randomUUID(),
    recoveryReceipt: Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join(""),
    createdAt,
    expiresAt: formatUtcInstant(dateFromEpochMillis(epochMillisOfUtcInstant(createdAt) + retentionMs)),
  };
  try { storage.setItem(prefix + record.clientSubmissionId, JSON.stringify(record)); }
  catch { throw new BookingRecoveryStorageError({ reason: "storage-unavailable" }); }
  const persisted = readBookingRecovery(storage, clock, record.clientSubmissionId);
  if (persisted.recoveryReceipt !== record.recoveryReceipt || persisted.createdAt !== record.createdAt || persisted.expiresAt !== record.expiresAt) {
    throw new BookingRecoveryStorageError({ reason: "storage-unavailable" });
  }
  return persisted;
}
